import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

interface AccountantInfoTooltipProps {
  content: React.ReactNode;
  label?: string;
  className?: string;
  iconClassName?: string;
  panelClassName?: string;
  align?: 'left' | 'right';
}

type TooltipPosition = {
  top: number;
  left: number;
};

const TOOLTIP_GAP = 8;
const VIEWPORT_PADDING = 8;

export const AccountantInfoTooltip: React.FC<AccountantInfoTooltipProps> = ({
  content,
  label = 'More information',
  className = '',
  iconClassName = 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300',
  panelClassName = 'w-72',
  align = 'right',
}) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const tooltipId = useId();

  const clearCloseTimer = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setOpen(false);
      setPosition(null);
    }, 120);
  };

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) return;

    const rect = trigger.getBoundingClientRect();
    const panelWidth = panel.offsetWidth;
    const panelHeight = panel.offsetHeight;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const showBelow = spaceBelow >= panelHeight + TOOLTIP_GAP || spaceBelow >= spaceAbove;

    const top = showBelow
      ? rect.bottom + TOOLTIP_GAP
      : rect.top - panelHeight - TOOLTIP_GAP;

    let left = align === 'right' ? rect.right - panelWidth : rect.left;
    left = Math.max(
      VIEWPORT_PADDING,
      Math.min(left, window.innerWidth - panelWidth - VIEWPORT_PADDING)
    );

    setPosition({ top, left });
  }, [align]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, content, updatePosition]);

  useEffect(() => {
    if (!open) return;

    const handleReposition = () => updatePosition();
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [open, updatePosition]);

  useEffect(
    () => () => {
      clearCloseTimer();
    },
    []
  );

  const handleOpen = () => {
    clearCloseTimer();
    setOpen(true);
  };

  const handleClose = () => {
    scheduleClose();
  };

  const tooltipPanel =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={panelRef}
            id={tooltipId}
            role="tooltip"
            style={{
              position: 'fixed',
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              visibility: position ? 'visible' : 'hidden',
            }}
            className={`z-[9999] max-w-[calc(100vw-16px)] rounded-lg border border-gray-200 bg-white p-3 text-xs leading-relaxed text-gray-700 shadow-xl dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 ${panelClassName}`}
            onMouseEnter={handleOpen}
            onMouseLeave={handleClose}
          >
            {content}
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <span className={`inline-flex items-center ${className}`}>
        <button
          ref={triggerRef}
          type="button"
          aria-label={label}
          aria-describedby={open ? tooltipId : undefined}
          onMouseEnter={handleOpen}
          onMouseLeave={handleClose}
          onFocus={handleOpen}
          onBlur={handleClose}
          onClick={() => {
            clearCloseTimer();
            setOpen((prev) => {
              if (prev) setPosition(null);
              return !prev;
            });
          }}
          className={`rounded p-0.5 transition-colors ${iconClassName}`}
        >
          <Info className="h-4 w-4" />
        </button>
      </span>
      {tooltipPanel}
    </>
  );
};

export const PLATFORM_OFFER_POLICY_TOOLTIP = (
  <>
    <p className="font-medium text-gray-900 dark:text-gray-100">How platform offers affect settlement</p>
    <p className="mt-2">
      Vendor payout = customer paid − platform commission after offer.
    </p>
    <p className="mt-2">
      Booking discounts reduce platform share only — not an extra vendor deduction.
    </p>
    <p className="mt-2">
      Example with 20% platform commission on consultation fee:
    </p>
    <ul className="mt-1 list-disc space-y-1 pl-4">
      <li>20% offer → 0% platform fee</li>
      <li>15% offer → 5% platform fee</li>
      <li>10% offer → 10% platform fee</li>
    </ul>
  </>
);

export const PAYOUT_POLICY_TOOLTIP = (
  <>
    All ledger entries are shown below. Only T+2 eligible entries appear in the settlement queue for payout.
  </>
);
